import { Rng } from '@sd/sim';
import {
  AdditiveBlending,
  BackSide,
  BufferGeometry,
  CanvasTexture,
  Color,
  Float32BufferAttribute,
  Group,
  Mesh,
  MeshBasicMaterial,
  Points,
  SRGBColorSpace,
  ShaderMaterial,
  SphereGeometry,
} from 'three';
import { LAYER_NO_OUTLINE } from '../outline';

/**
 * The sky behind the map (#99, the author's reference of 21.10.2026): deep
 * blue space with pink and violet nebulae, a field of stars of every size,
 * and dust drifting slowly over the system. Seeded, so a system always
 * gets the same sky; nothing here is game state.
 */
/**
 * The nebula, painted once into an equirectangular texture when the system
 * is shown: a noise shader over the whole sky every frame was too slow for
 * software rendering (the CI's screenshots), and the sky never changes.
 */
const SKY_W = 512;
const SKY_H = 256;

/** Value noise in 3D from a seeded permutation: smooth, cheap, deterministic. */
function makeNoise(rng: Rng): (x: number, y: number, z: number) => number {
  const perm = rng.shuffle(Array.from({ length: 256 }, (_, i) => i));
  const p = [...perm, ...perm];
  const values = Array.from({ length: 256 }, () => rng.range(0, 1));
  const hash = (x: number, y: number, z: number) =>
    values[p[p[p[x & 255]! + (y & 255)]! + (z & 255)]!]!;
  const smooth = (t: number) => t * t * (3 - 2 * t);
  return (x, y, z) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const zi = Math.floor(z);
    const u = smooth(x - xi);
    const v = smooth(y - yi);
    const w = smooth(z - zi);
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    return lerp(
      lerp(
        lerp(hash(xi, yi, zi), hash(xi + 1, yi, zi), u),
        lerp(hash(xi, yi + 1, zi), hash(xi + 1, yi + 1, zi), u),
        v,
      ),
      lerp(
        lerp(hash(xi, yi, zi + 1), hash(xi + 1, yi, zi + 1), u),
        lerp(hash(xi, yi + 1, zi + 1), hash(xi + 1, yi + 1, zi + 1), u),
        v,
      ),
      w,
    );
  };
}

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

function nebulaTexture(rng: Rng): CanvasTexture {
  const noise = makeNoise(rng);
  const fbm = (x: number, y: number, z: number) => {
    let v = 0;
    let a = 0.5;
    for (let i = 0; i < 5; i++) {
      v += a * noise(x, y, z);
      x *= 2.03;
      y *= 2.03;
      z *= 2.03;
      a *= 0.5;
    }
    return v;
  };
  const deep = new Color('#05041a');
  const blue = new Color('#1b2a8f');
  const violet = new Color('#5b23b8');
  const magenta = new Color('#e0249a');
  const canvas = document.createElement('canvas');
  canvas.width = SKY_W;
  canvas.height = SKY_H;
  const ctx = canvas.getContext('2d')!;
  const image = ctx.createImageData(SKY_W, SKY_H);
  const c = new Color();
  for (let j = 0; j < SKY_H; j++) {
    const lat = (j / (SKY_H - 1) - 0.5) * Math.PI;
    for (let i = 0; i < SKY_W; i++) {
      const lon = (i / SKY_W) * Math.PI * 2;
      // A point on the sphere, so the texture wraps without a seam.
      const x = Math.cos(lat) * Math.cos(lon) * 2.2 + 11;
      const y = Math.sin(lat) * 2.2 + 23;
      const z = Math.cos(lat) * Math.sin(lon) * 2.2 + 37;
      const cloud = smoothstep(0.4, 0.62, fbm(x, y, z));
      const wisps = smoothstep(0.38, 0.6, fbm(x * 2.4 + 7.3, y * 2.4 + 7.3, z * 2.4 + 7.3));
      const hot = smoothstep(0.52, 0.66, fbm(x * 1.6 + 3.1, y * 1.6 + 3.1, z * 1.6 + 3.1)) * cloud;
      c.copy(deep)
        .lerp(blue, cloud * 0.55)
        .lerp(violet, cloud * wisps * 0.75)
        .lerp(magenta, hot * 0.85);
      const k = (j * SKY_W + i) * 4;
      image.data[k] = c.r * 255;
      image.data[k + 1] = c.g * 255;
      image.data[k + 2] = c.b * 255;
      image.data[k + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/** A big sphere around everything, the nebula painted on its inside. */
function nebula(rng: Rng): Mesh {
  const material = new MeshBasicMaterial({
    map: nebulaTexture(rng),
    side: BackSide,
    depthWrite: false,
  });
  const sky = new Mesh(new SphereGeometry(380, 48, 32), material);
  sky.renderOrder = -10;
  sky.layers.set(LAYER_NO_OUTLINE);
  return sky;
}

const STAR_VERTEX = /* glsl */ `
  attribute float size;
  attribute vec3 tint;
  varying vec3 vTint;
  void main() {
    vTint = tint;
    vec4 view = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size;
    gl_Position = projectionMatrix * view;
  }
`;
const STAR_FRAGMENT = /* glsl */ `
  varying vec3 vTint;
  uniform float opacity;
  void main() {
    // A round point with a soft edge: a star, or a mote of dust.
    float d = length(gl_PointCoord - 0.5);
    float a = smoothstep(0.5, 0.0, d);
    gl_FragColor = vec4(vTint, a * opacity);
  }
`;

/** Points of light: `count` of them where `at` puts them, sized and tinted from the seed. */
function points(
  rng: Rng,
  count: number,
  at: () => [number, number, number],
  size: () => number,
  tints: readonly string[],
  opacity: number,
): Points {
  const positions: number[] = [];
  const sizes: number[] = [];
  const colors: number[] = [];
  const c = new Color();
  for (let i = 0; i < count; i++) {
    positions.push(...at());
    sizes.push(size());
    c.set(rng.pick(tints));
    colors.push(c.r, c.g, c.b);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('size', new Float32BufferAttribute(sizes, 1));
  geometry.setAttribute('tint', new Float32BufferAttribute(colors, 3));
  const material = new ShaderMaterial({
    vertexShader: STAR_VERTEX,
    fragmentShader: STAR_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: { opacity: { value: opacity } },
  });
  const p = new Points(geometry, material);
  p.layers.set(LAYER_NO_OUTLINE);
  return p;
}

export interface Sky {
  readonly root: Group;
  /** Dust drifts with the seconds since start. */
  update(seconds: number): void;
}

export function buildSky(seed: string, mapRadius: number): Sky {
  const rng = Rng.fromSeed(`sky:${seed}`);
  const root = new Group();
  root.add(nebula(rng));
  const onSphere = (min: number, max: number) => (): [number, number, number] => {
    const u = rng.range(-1, 1);
    const theta = rng.range(0, Math.PI * 2);
    const s = Math.sqrt(1 - u * u);
    const d = rng.range(min, max);
    return [d * s * Math.cos(theta), d * u, d * s * Math.sin(theta)];
  };
  // Many faint stars and a few bright ones, white to blue to warm.
  root.add(
    points(
      rng,
      4500,
      onSphere(250, 360),
      () => rng.range(1, 2.6),
      ['#ffffff', '#cfd8ff', '#ffe6f4'],
      0.9,
    ),
  );
  root.add(
    points(
      rng,
      260,
      onSphere(250, 360),
      () => rng.range(3, 6),
      ['#ffffff', '#bcd2ff', '#ffd2ea'],
      1,
    ),
  );
  // Dust over the system: tiny motes above the map, drifting round the star.
  const extent = mapRadius * 1.9;
  const dust = points(
    rng,
    1400,
    () => [rng.range(-extent, extent), rng.range(0.6, 9), rng.range(-extent, extent)],
    () => rng.range(1, 2.4),
    ['#b9a6ff', '#ff9ad6', '#9fc4ff'],
    0.45,
  );
  root.add(dust);
  return {
    root,
    update(seconds) {
      dust.rotation.y = seconds * 0.004;
    },
  };
}
